import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api/client";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { Tab, Switch } from "@headlessui/react";
import type { Document, HistoryLog, Identity, User } from "../types/api";
import {
  UserIcon,
  DocumentTextIcon,
  ShoppingBagIcon,
  ClockIcon,
  EnvelopeIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";
import EvidenceModal from "../components/EvidenceModal";
import OrdersTable from "../components/OrdersTable";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

// Add type definition for custom Clarity attribute
declare module 'react' {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    'data-clarity-mask'?: string;
  }
}

// Helper to safely format history values
const formatHistoryValue = (val: unknown) => {
  if (val === null || val === undefined) return "N/A";
  if (typeof val === "object") {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return String(val);
};

// Fraud indicator type labels and descriptions
const FRAUD_TYPE_LABELS: Record<string, { label: string; description: string }> = {
  duplicate_return: { label: "Duplicate Returns", description: "Same item returned multiple times" },
  return_exceeds_order: { label: "Return Exceeds Order", description: "Return amount higher than order total" },
  contract_id_mismatch: { label: "Contract Mismatch", description: "Contract IDs don't match across files" },
  price_anomaly: { label: "Price Anomalies", description: "Unusual price compared to historical data" },
  amount_discrepancy: { label: "Amount Discrepancies", description: "Calculated amounts don't match stated amounts" },
  date_inconsistency: { label: "Date Inconsistencies", description: "Return dates before order dates" },
  missing_tracking: { label: "Missing Tracking", description: "Closed returns without tracking info" },
  order_id_mismatch: { label: "Order ID Issues", description: "Invalid or unknown order IDs" },
  suspicious_pattern: { label: "Suspicious Patterns", description: "Behavioral patterns indicating fraud" },
  invoice_manipulation: { label: "Invoice Manipulation", description: "Invoice totals don't match CSV data" },
  missing_invoice: { label: "Missing Invoices", description: "Returns without corresponding invoices" },
  orphan_record: { label: "Orphan Records", description: "Records without cross-references" },
  asin_mismatch: { label: "ASIN Mismatch", description: "Product ASIN inconsistencies" },
  invalid_state_transition: { label: "Invalid State", description: "Invalid receivable state changes" },
};

// Helper to group indicators by type (only non-dismissed indicators)
type FraudIndicator = { type: string; severity: string; description: string; orderId?: string; dismissed?: boolean };
type FraudIndicatorWithIndex = FraudIndicator & { originalIndex: number };

const groupIndicatorsByType = (indicators: FraudIndicator[]) => {
  const groups: Record<string, FraudIndicatorWithIndex[]> = {};
  // Only group non-dismissed indicators, preserving original index
  indicators.forEach((indicator, originalIndex) => {
    if (indicator.dismissed) return;
    if (!groups[indicator.type]) {
      groups[indicator.type] = [];
    }
    groups[indicator.type].push({ ...indicator, originalIndex });
  });
  // Sort groups by count (most indicators first)
  return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
};

// Get highest severity in a group
const getGroupSeverity = (indicators: FraudIndicator[]) => {
  if (indicators.some(i => i.severity === 'critical' && !i.dismissed)) return 'critical';
  if (indicators.some(i => i.severity === 'high' && !i.dismissed)) return 'high';
  if (indicators.some(i => i.severity === 'medium' && !i.dismissed)) return 'medium';
  return 'low';
};

export default function ClaimantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useAuthStore((state) => state.role);
  const isSuperAdmin = role === 'super_admin';
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");

  // Communication state
  const [emailSubject, setEmailSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");

  // Fraud indicator groups expansion state
  const [expandedFraudGroups, setExpandedFraudGroups] = useState<Set<string>>(new Set());

  // Dismiss fraud indicator modal state
  const [dismissModal, setDismissModal] = useState<{
    isOpen: boolean;
    mode: 'single' | 'category';
    index?: number;
    type?: string;
    typeLabel?: string;
    count?: number;
  }>({ isOpen: false, mode: 'single' });
  const [dismissReason, setDismissReason] = useState("");

  // Queries
  const { data: claimantData, isLoading: isLoadingClaimant } = useQuery({
    queryKey: ["claimant", id],
    queryFn: async () => (await api.get(`/admin/claimants/${id}`)).data.data,
  });

  const { data: historyData } = useQuery({
    queryKey: ["claimantHistory", id],
    queryFn: async () => (await api.get(`/admin/claimants/${id}/history`)).data.data,
  });

  const { data: communicationData } = useQuery({
    queryKey: ["claimantCommunication", id],
    queryFn: async () => (await api.get(`/admin/claimants/${id}/communication`)).data.data,
  });

  const { data: antifraudData, refetch: refetchAntifraud } = useQuery({
    queryKey: ["claimantAntifraud", id],
    queryFn: async () => (await api.get(`/admin/claimants/${id}/antifraud`)).data.data,
  });

  const { data: docusignData, isLoading: isLoadingDocusign } = useQuery({
    queryKey: ["claimantDocusign", id],
    queryFn: async () => (await api.get(`/admin/claimants/${id}/docusign`)).data.data,
    enabled: !!id,
  });

  // Mutations
  const updateClaimantMutation = useMutation({
    mutationFn: async (data: { updates: Partial<User>; reason: string; evidenceDocumentId?: string }) => {
      return api.put(`/admin/claimants/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claimant", id] });
      queryClient.invalidateQueries({ queryKey: ["claimantHistory", id] });
      toast.success("Claimant updated successfully");
      setIsEvidenceModalOpen(false);
      setEditingField(null);
    },
    onError: () => {
      toast.error("Failed to update claimant");
    },
  });

  const deleteClaimantMutation = useMutation({
    // Hard delete if isHard is true, otherwise soft delete
    mutationFn: async (isHard: boolean) => {
       if (isHard) {
           return api.delete(`/admin/claimants/${id}?hard=true`); 
       } else {
           return api.delete(`/admin/claimants/${id}`);
       }
    },
    onSuccess: (_, isHard) => {
      if (isHard) {
        toast.success("Claimant permanently deleted");
        navigate("/claimants"); // Redirect to list
      } else {
        queryClient.invalidateQueries({ queryKey: ["claimant", id] });
        queryClient.invalidateQueries({ queryKey: ["claimantHistory", id] });
        toast.success("Claimant soft deleted");
      }
    },
    onError: () => {
      toast.error("Failed to delete claimant");
    },
  });

  const restoreClaimantMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/admin/claimants/${id}/restore`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claimant", id] });
      queryClient.invalidateQueries({ queryKey: ["claimantHistory", id] });
      toast.success("Claimant restored");
    },
    onError: () => {
      toast.error("Failed to restore claimant");
    },
  });

  const sendEmailMutation = useMutation({
    mutationFn: async (data: { userId: string; subject: string; content: string }) => {
      return api.post("/admin/email", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claimantCommunication", id] });
      toast.success("Email sent successfully");
      setEmailSubject("");
      setEmailContent("");
    },
    onError: () => {
      toast.error("Failed to send email");
    },
  });

  const triggerAntifraudMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/admin/claimants/${id}/antifraud`);
    },
    onSuccess: () => {
      toast.success("Antifraud analysis queued successfully");
      refetchAntifraud();
    },
    onError: () => {
      toast.error("Failed to trigger antifraud analysis");
    },
  });

  const dismissFraudIndicatorMutation = useMutation({
    mutationFn: async ({ index, reason }: { index: number; reason: string }) => {
      return api.post(`/admin/claimants/${id}/antifraud/dismiss`, { index, reason });
    },
    onSuccess: () => {
      toast.success("Fraud indicator dismissed");
      refetchAntifraud();
      setDismissModal({ isOpen: false, mode: 'single' });
      setDismissReason("");
    },
    onError: () => {
      toast.error("Failed to dismiss indicator");
    },
  });

  const dismissFraudIndicatorsByTypeMutation = useMutation({
    mutationFn: async ({ type, reason }: { type: string; reason: string }) => {
      return api.post(`/admin/claimants/${id}/antifraud/dismiss-type`, { type, reason });
    },
    onSuccess: (data) => {
      const count = data.data?.dismissedCount || 0;
      toast.success(`${count} indicator(s) dismissed`);
      refetchAntifraud();
      setDismissModal({ isOpen: false, mode: 'single' });
      setDismissReason("");
    },
    onError: () => {
      toast.error("Failed to dismiss indicators");
    },
  });

  const resendDocusignEmailMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/admin/claimants/${id}/docusign/resend`);
    },
    onSuccess: () => {
      toast.success("DocuSign email resent successfully");
    },
    onError: () => {
      toast.error("Failed to resend DocuSign email");
    },
  });

  // Download contract mutation
  const downloadContractMutation = useMutation({
    mutationFn: async () => {
      const response = await api.get(`/admin/claimants/${id}/docusign/download`, {
        responseType: "blob",
      });
      return response;
    },
    onSuccess: (response) => {
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Claroclaim_Vertrag.pdf';
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Contract downloaded successfully");
    },
    onError: () => {
      toast.error("Failed to download contract");
    },
  });

  // Identity evidence state and mutation
  const [isIdentityEvidenceModalOpen, setIsIdentityEvidenceModalOpen] = useState(false);
  const [identityEvidenceImages, setIdentityEvidenceImages] = useState<Array<{ type: string; data: string }>>([]);

  const identityEvidenceMutation = useMutation({
    mutationFn: async () => {
      return api.get(`/admin/claimants/${id}/docusign/identity-evidence`);
    },
    onSuccess: (response) => {
      setIdentityEvidenceImages(response.data.data.images || []);
      setIsIdentityEvidenceModalOpen(true);
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message = axiosError.response?.data?.message || "Failed to load identity evidence";
      toast.error(message);
    },
  });

  // Resend DocuSign confirmation modal
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);

  // Correct DocuSign envelope mutation (void and recreate with corrected details)
  const [isCorrectModalOpen, setIsCorrectModalOpen] = useState(false);
  const [correctionData, setCorrectionData] = useState({
    name: "",
    street: "",
    city: "",
    postalCode: "",
    country: "DE",
  });

  const correctDocusignMutation = useMutation({
    mutationFn: async (data: typeof correctionData) => {
      return api.post(`/admin/claimants/${id}/docusign/correct`, data);
    },
    onSuccess: () => {
      toast.success("Envelope recreated with corrected details. User can now retry signing.");
      setIsCorrectModalOpen(false);
      setCorrectionData({ name: "", street: "", city: "", postalCode: "", country: "DE" });
      queryClient.invalidateQueries({ queryKey: ["claimant", id] });
      queryClient.invalidateQueries({ queryKey: ["claimant-docusign", id] });
    },
    onError: () => {
      toast.error("Failed to correct envelope");
    },
  });

  // Helper to get current identity data for pre-populating the correction modal
  const getSelectedIdentityData = () => {
    if (identities && identities.length > 0) {
      const selected = identities.find((i: Identity) => i.selected) || identities[0];
      return {
        name: selected.name || user?.name || "",
        street: selected.address?.street || "",
        city: selected.address?.city || selected.city || "",
        postalCode: selected.address?.postalCode || selected.postalCode || "",
        country: selected.address?.country || selected.country || "DE",
      };
    }
    return {
      name: user?.name || "",
      street: "",
      city: "",
      postalCode: "",
      country: "DE",
    };
  };

  if (isLoadingClaimant) return <div>Loading...</div>;
  if (!claimantData) return <div>Error loading claimant</div>;

  const { user, documents, orders, rejectionStats, purchasePrice, identities } = claimantData;

  const handleEditClick = (field: string, currentValue: string) => {
    setEditingField(field);
    setEditValue(currentValue || "");
    setIsEvidenceModalOpen(true);
  };

  const handleUpdateConfirm = (reason: string, file: File | null) => {
    // In a real app, we would upload the file first and get the ID
    // For now, we'll just simulate it or ignore the file
    const updates = { [editingField!]: editValue };
    updateClaimantMutation.mutate({
      updates,
      reason,
      evidenceDocumentId: file ? "uploaded-doc-id" : undefined
    });
  };

  const handleSoftDelete = () => {
      if (window.confirm("Are you sure you want to soft delete this user? They will be marked as deleted but kept in the database.")) {
          deleteClaimantMutation.mutate(false);
      }
  };
  
  const handleRestore = () => {
      if (window.confirm("Are you sure you want to restore this user?")) {
          restoreClaimantMutation.mutate();
      }
  };

  const handleHardDelete = () => {
      if (window.confirm("WARNING: This action is permanent! Are you sure you want to completely remove this user and all their data?")) {
          if (window.confirm("Final confirmation: This cannot be undone.")) {
            deleteClaimantMutation.mutate(true);
          }
      }
  };

  const handleSendEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    sendEmailMutation.mutate({
      userId: id,
      subject: emailSubject,
      content: emailContent
    });
  };

  // Download document with authentication
  const handleDownload = async (documentId: string, originalName: string) => {
    try {
      const response = await api.get(`/documents/${documentId}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download document");
    }
  };

  // Check for issues to show alert symbols on tabs
  const hasFraudIssue = antifraudData?.result?.summary?.riskLevel &&
    ['high', 'critical', 'medium'].includes(antifraudData.result.summary.riskLevel);
  const hasDocusignError = user?.docusign?.deliveryStatus === 'failed' || user?.docusign?.deliveryStatus === 'autoresponded' || user?.docusign?.deliveryStatus === 'authfailed';

  const tabs = [
    { name: "Overview", icon: UserIcon, hasAlert: hasFraudIssue },
    { name: "Orders", icon: ShoppingBagIcon, hasAlert: false },
    { name: "Documents", icon: DocumentTextIcon, hasAlert: false },
    { name: "Identities", icon: UserIcon, hasAlert: false },
    { name: "DocuSign", icon: ClipboardDocumentCheckIcon, hasAlert: hasDocusignError },
    { name: "History", icon: ClockIcon, hasAlert: false },
    { name: "Communication", icon: EnvelopeIcon, hasAlert: false },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:truncate sm:text-3xl sm:tracking-tight">
            {user.name || "Unknown User"}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <span
            className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold leading-5 ${
              user.isVerified
                ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                : "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
            }`}
          >
            {user.isVerified ? "Verified" : "Pending"}
          </span>
        </div>
      </div>

      <Tab.Group>
        <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 dark:bg-blue-800/30 p-1">
          {tabs.map((tab) => (
            <Tab
              key={tab.name}
              className={({ selected }) =>
                classNames(
                  "w-full rounded-lg py-2.5 text-sm font-medium leading-5",
                  "ring-white dark:ring-gray-400 ring-opacity-60 ring-offset-2 ring-offset-blue-400 dark:ring-offset-gray-800 focus:outline-none focus:ring-2",
                  selected
                    ? "bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-300 shadow"
                    : "text-blue-100 dark:text-blue-200 hover:bg-white/[0.12] dark:hover:bg-white/[0.08] hover:text-white"
                )
              }
            >
              <div className="flex items-center justify-center gap-2">
                <tab.icon className="h-5 w-5" />
                {tab.name}
                {tab.hasAlert && (
                  <ExclamationTriangleIcon className="h-4 w-4 text-orange-500" />
                )}
              </div>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels className="mt-2">
          {/* Overview Panel */}
          <Tab.Panel className="rounded-xl bg-white dark:bg-gray-800 p-3 shadow ring-white dark:ring-gray-700 ring-opacity-60 ring-offset-2 ring-offset-blue-400 dark:ring-offset-gray-900 focus:outline-none">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">Claimant Information</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">Personal details and application status.</p>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200 dark:sm:divide-gray-700">
                {[
                  { label: "Provider", field: "provider", value: user.provider },
                  { label: "Full Name", field: "name", value: user.name },
                  { label: "Email", field: "email", value: user.email },
                  { label: "IBAN", field: "iban", value: user.iban },
                  { label: "Address", field: "address", value: user.address },
                ].map((item) => (
                  <div key={item.field} className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{item.label}</dt>
                    <dd className="mt-1 flex text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                      <span className="flex-grow" data-clarity-mask="True">{item.value || "N/A"}</span>
                      <span className="ml-4 flex-shrink-0 flex gap-2">
                        {item.value && (
                          <button
                            type="button"
                            className="rounded-md bg-white dark:bg-gray-700 font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            onClick={() => {
                              navigator.clipboard.writeText(item.value);
                              toast.success(`${item.label} copied to clipboard`);
                            }}
                          >
                            <ClipboardDocumentCheckIcon className="h-4 w-4" />
                            <span className="sr-only">Copy {item.label}</span>
                          </button>
                        )}
                        <button
                          type="button"
                          className="rounded-md bg-white dark:bg-gray-700 font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          onClick={() => handleEditClick(item.field, item.value)}
                        >
                          <PencilIcon className="h-4 w-4" />
                          <span className="sr-only">Update {item.label}</span>
                        </button>
                      </span>
                    </dd>
                  </div>
                ))}
                <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Progress Stage</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                    {(() => {
                      const completedAt = user.progress?.completedAt || {};
                      if (completedAt.payoutExecuted) return "Payout Executed ✓✓";
                      if (completedAt.payoutScheduled) return "Payout Scheduled ✓";
                      if (completedAt.contractCompleted) return "Contract Completed ✓";
                      if (completedAt.contractStarted) return "Contract Started";
                      if (completedAt.bankingProvided) return "Banking Provided";
                      if (completedAt.documentsUploaded) return "Documents Uploaded";
                      if (completedAt.emailConfirmed) return "Email Confirmed";
                      if (completedAt.accountCreated) return "Account Created";
                      return "Just Registered";
                    })()}
                  </dd>
                </div>
                {purchasePrice !== null && user.progress?.completedAt?.documentsUploaded && (
                  <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Calculated Purchase Price</dt>
                    <dd className="mt-1 text-sm font-bold text-green-600 dark:text-green-400 sm:col-span-2 sm:mt-0">
                      {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(purchasePrice)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
            
            <div className="mt-8 px-4 py-5 sm:px-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">Manual State Management</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Special case for isVerified */}
                <div className="relative flex items-center justify-between p-4 border rounded-lg border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/10">
                  <div className="min-w-0 flex-1 text-sm">
                    <label className="font-medium text-gray-700 dark:text-gray-200 select-none">
                      Is Verified (Global Status)
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Controls the main verified badge
                    </p>
                  </div>
                  <Switch
                    checked={user.isVerified}
                    onChange={(checked: boolean) => {
                      if (window.confirm(`Are you sure you want to manually set isVerified to ${checked}?`)) {
                        updateClaimantMutation.mutate({
                          updates: { isVerified: checked },
                          reason: `Manual state change by admin: isVerified -> ${checked}`
                        });
                      }
                    }}
                    className={`${
                      user.isVerified ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 dark:ring-offset-gray-900`}
                  >
                    <span className="sr-only">Toggle verification status</span>
                    <span
                      aria-hidden="true"
                      className={`${
                        user.isVerified ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </Switch>
                </div>

                {[
                  { key: 'accountCreated', label: 'Account Created' },
                  { key: 'emailConfirmed', label: 'Email Confirmed' },
                  { key: 'documentsUploaded', label: 'Documents Uploaded' },
                  { key: 'bankingProvided', label: 'Banking Provided' },
                  { key: 'contractStarted', label: 'Contract Started' },
                  { key: 'contractCompleted', label: 'Contract Completed' },
                  { key: 'payoutScheduled', label: 'Payout Scheduled' },
                  { key: 'payoutExecuted', label: 'Payout Executed' },
                ].map((step) => {
                  const completedAt = user.progress?.completedAt || {};
                  const isCompleted = !!completedAt[step.key as keyof typeof completedAt];
                  const completedDate = completedAt[step.key as keyof typeof completedAt];

                  return (
                    <div key={step.key} className="relative flex items-center justify-between p-4 border rounded-lg border-gray-200 dark:border-gray-700">
                      <div className="min-w-0 flex-1 text-sm">
                        <label className="font-medium text-gray-700 dark:text-gray-200 select-none">
                          {step.label}
                        </label>
                        {isCompleted && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {new Date(completedDate).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <Switch
                        checked={isCompleted}
                        onChange={(checked: boolean) => {
                          const newValue = checked ? new Date().toISOString() : null;
                          const updates = { [`progress.completedAt.${step.key}`]: newValue };
                          
                          if (window.confirm(`Are you sure you want to manually change the state of ${step.label}?`)) {
                            updateClaimantMutation.mutate({
                              updates,
                              reason: `Manual state change by admin: ${step.label} -> ${checked ? 'Completed' : 'Pending'}`
                            });
                          }
                        }}
                        className={`${
                          isCompleted ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 dark:ring-offset-gray-900`}
                      >
                        <span className="sr-only">Toggle {step.label}</span>
                        <span
                          aria-hidden="true"
                          className={`${
                            isCompleted ? 'translate-x-5' : 'translate-x-0'
                          } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                      </Switch>
                    </div>
                  );
                })}
              </div>
              </div>


            {/* Antifraud Analysis Section */}
            <div className="mt-8 px-4 py-5 sm:px-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5" />
                Antifraud Analysis
              </h3>
              <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                {antifraudData?.status === 'not_analyzed' ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        No antifraud analysis has been run for this user yet.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => triggerAntifraudMutation.mutate()}
                      disabled={triggerAntifraudMutation.isPending}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      <ShieldCheckIcon className="h-4 w-4 mr-2" />
                      {triggerAntifraudMutation.isPending ? 'Queuing...' : 'Run Analysis'}
                    </button>
                  </div>
                ) : antifraudData?.result ? (
                  <div className="space-y-4">
                    {/* Risk Level Badge */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          antifraudData.result.summary?.riskLevel === 'high' || antifraudData.result.summary?.riskLevel === 'critical'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                            : antifraudData.result.summary?.riskLevel === 'medium'
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                            : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                        }`}>
                          {(antifraudData.result.summary?.riskLevel === 'high' || antifraudData.result.summary?.riskLevel === 'critical') && <ExclamationTriangleIcon className="h-4 w-4 mr-1" />}
                          Risk: {antifraudData.result.summary?.riskLevel?.toUpperCase() || 'UNKNOWN'}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          Score: {antifraudData.result.summary?.riskScore || 0}/100
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => triggerAntifraudMutation.mutate()}
                        disabled={triggerAntifraudMutation.isPending}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        <ArrowPathIcon className="h-4 w-4 mr-1" />
                        {triggerAntifraudMutation.isPending ? 'Queuing...' : 'Re-run'}
                      </button>
                    </div>

                    {/* Analysis Details */}
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Analyzed: {new Date(antifraudData.analyzedAt).toLocaleString()}
                    </div>

                    {/* Fraud Indicators - Grouped by Category */}
                    {(() => {
                      const activeIndicators = antifraudData.result.indicators?.filter((i: FraudIndicator) => !i.dismissed) || [];
                      const groupedIndicators = groupIndicatorsByType(antifraudData.result.indicators || []);

                      if (activeIndicators.length === 0) {
                        return (
                          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                            <ShieldCheckIcon className="h-5 w-5" />
                            <span>No active fraud indicators</span>
                          </div>
                        );
                      }

                      return (
                        <div className="mt-3">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Fraud Indicators ({activeIndicators.length})
                          </h4>
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {groupedIndicators.map(([type, indicators]) => {
                              const isExpanded = expandedFraudGroups.has(type);
                              const groupSeverity = getGroupSeverity(indicators);
                              const typeInfo = FRAUD_TYPE_LABELS[type] || { label: type, description: '' };
                              const activeCount = indicators.length;

                              return (
                                <div key={type} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                  {/* Group Header */}
                                  <button
                                    onClick={() => {
                                      const newExpanded = new Set(expandedFraudGroups);
                                      if (isExpanded) {
                                        newExpanded.delete(type);
                                      } else {
                                        newExpanded.add(type);
                                      }
                                      setExpandedFraudGroups(newExpanded);
                                    }}
                                    className={`w-full px-3 py-2 flex items-center justify-between text-left cursor-pointer ${
                                      groupSeverity === 'critical' || groupSeverity === 'high'
                                        ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
                                        : groupSeverity === 'medium'
                                        ? 'bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
                                        : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                                  >
                                  <div className="flex items-center gap-2">
                                    {isExpanded ? (
                                      <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                                    ) : (
                                      <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                                    )}
                                    <span className={`font-medium ${
                                      groupSeverity === 'critical' || groupSeverity === 'high'
                                        ? 'text-red-700 dark:text-red-400'
                                        : groupSeverity === 'medium'
                                        ? 'text-yellow-700 dark:text-yellow-400'
                                        : 'text-gray-700 dark:text-gray-300'
                                    }`}>
                                      {typeInfo.label}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      ({activeCount})
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      groupSeverity === 'critical' || groupSeverity === 'high'
                                        ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                                        : groupSeverity === 'medium'
                                        ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}>
                                      {groupSeverity.toUpperCase()}
                                    </span>
                                    {activeCount > 0 && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDismissModal({
                                            isOpen: true,
                                            mode: 'category',
                                            type,
                                            typeLabel: typeInfo.label,
                                            count: activeCount,
                                          });
                                        }}
                                        className="text-xs px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                                        title={`Dismiss all ${typeInfo.label}`}
                                      >
                                        Dismiss All
                                      </button>
                                    )}
                                  </div>
                                </button>

                                {/* Group Description */}
                                {isExpanded && typeInfo.description && (
                                  <div className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                                    {typeInfo.description}
                                  </div>
                                )}

                                {/* Expanded Indicators */}
                                {isExpanded && (
                                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {indicators.map((indicator) => {
                                      // Use the originalIndex that was preserved during grouping
                                      const originalIdx = (indicator as FraudIndicatorWithIndex).originalIndex;

                                      return (
                                        <div
                                          key={originalIdx}
                                          className={`p-2 text-sm relative group ${
                                            indicator.dismissed
                                              ? 'bg-gray-100 dark:bg-gray-800 opacity-60'
                                              : 'bg-white dark:bg-gray-900'
                                          }`}
                                        >
                                          {!indicator.dismissed && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setDismissModal({
                                                  isOpen: true,
                                                  mode: 'single',
                                                  index: originalIdx,
                                                });
                                              }}
                                              className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                              title="Dismiss Indicator"
                                            >
                                              <XMarkIcon className="h-4 w-4" />
                                            </button>
                                          )}
                                          <div className={`flex items-center gap-2 ${indicator.dismissed ? 'line-through decoration-gray-400' : ''}`}>
                                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                              indicator.dismissed
                                                ? 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                                                : indicator.severity === 'high' || indicator.severity === 'critical'
                                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                                : indicator.severity === 'medium'
                                                ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                            }`}>
                                              {indicator.severity.toUpperCase()}
                                            </span>
                                          </div>
                                          <p className={`text-gray-600 dark:text-gray-400 mt-1 ${indicator.dismissed ? 'line-through decoration-gray-400' : ''}`}>
                                            {indicator.description}
                                          </p>
                                          {indicator.orderId && (
                                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Order: {indicator.orderId}</p>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })()}

                    {/* Summary Stats */}
                    {antifraudData.result.summary && (
                      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
                          <div className="text-gray-500 dark:text-gray-400">Orders Analyzed</div>
                          <div className="font-medium text-gray-900 dark:text-white">{antifraudData.result.summary.totalOrdersAnalyzed || 0}</div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
                          <div className="text-gray-500 dark:text-gray-400">Invoices Parsed</div>
                          <div className="font-medium text-gray-900 dark:text-white">{antifraudData.result.summary.invoicesParsed || 0}</div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
                          <div className="text-gray-500 dark:text-gray-400">Cross-validated</div>
                          <div className="font-medium text-gray-900 dark:text-white">{antifraudData.result.summary.ordersCrossValidated || 0}</div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
                          <div className="text-gray-500 dark:text-gray-400">Coverage</div>
                          <div className="font-medium text-gray-900 dark:text-white">{antifraudData.result.summary.invoiceCoveragePercent?.toFixed(0) || 0}%</div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {antifraudData?.status === 'processing' ? 'Analysis in progress...' : 'Analysis queued...'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => refetchAntifraud()}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <ArrowPathIcon className="h-4 w-4 mr-1" />
                      Refresh
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 px-4 py-5 sm:px-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium leading-6 text-red-600 dark:text-red-400 mb-4">Danger Zone</h3>
              <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-lg p-4">
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Soft Delete User</h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {user.deletedAt 
                                    ? `Deleted on ${new Date(user.deletedAt).toLocaleString()}` 
                                    : "Mark user as deleted but keep data."}
                            </p>
                        </div>
                        {user.deletedAt ? (
                            <button
                                type="button"
                                onClick={handleRestore}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                                <ArrowPathIcon className="h-4 w-4 mr-2" />
                                Restore
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleSoftDelete}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                <TrashIcon className="h-4 w-4 mr-2" />
                                Soft Delete
                            </button>
                        )}
                    </div>
                    
                    <div className="border-t border-red-200 dark:border-red-900/30 pt-4 flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-medium text-red-800 dark:text-red-300">Hard Delete User</h4>
                            <p className="text-sm text-red-600 dark:text-red-400">
                                <strong>Warning:</strong> This action is permanent and cannot be undone.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleHardDelete}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                            <TrashIcon className="h-4 w-4 mr-2" />
                            Hard Delete
                        </button>
                    </div>
                </div>
              </div>
            </div>
          </Tab.Panel>

          {/* Orders Panel */}
          <Tab.Panel className="rounded-xl bg-white dark:bg-gray-800 p-3 shadow ring-white dark:ring-gray-700 ring-opacity-60 ring-offset-2 ring-offset-blue-400 dark:ring-offset-gray-900 focus:outline-none">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">Orders</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">Extracted from Amazon Data</p>
            </div>
            <div className="px-4 pb-5">
              {orders.length > 0 ? (
                <OrdersTable orders={orders} rejectionStats={rejectionStats} identities={identities} />
              ) : (
                <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">No orders found</div>
              )}
            </div>
          </Tab.Panel>

          {/* Documents Panel */}
          <Tab.Panel className="rounded-xl bg-white dark:bg-gray-800 p-3 shadow ring-white dark:ring-gray-700 ring-opacity-60 ring-offset-2 ring-offset-blue-400 dark:ring-offset-gray-900 focus:outline-none">
            <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-700">
              {documents.map((doc: Document) => (
                <li key={doc._id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-indigo-600 dark:text-indigo-400 truncate">{doc.originalName}</div>
                    <div className="ml-2 flex-shrink-0 flex">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        doc.status === "completed"
                          ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                          : "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
                      }`}>
                        {doc.status}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between">
                    <div className="sm:flex">
                      <p className="flex items-center text-sm text-gray-500 dark:text-gray-400">{doc.documentType}</p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400 sm:mt-0 gap-4">
                      <p>Uploaded on <time dateTime={doc.createdAt?.toString()}>{doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : 'N/A'}</time></p>
                      {isSuperAdmin && (
                        <button
                          onClick={() => handleDownload(doc._id, doc.originalName)}
                          className="inline-flex items-center gap-1 font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          Download
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Tab.Panel>

          {/* Identities Panel */}
          <Tab.Panel className="rounded-xl bg-white dark:bg-gray-800 p-3 shadow ring-white dark:ring-gray-700 ring-opacity-60 ring-offset-2 ring-offset-blue-400 dark:ring-offset-gray-900 focus:outline-none">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">Identities</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">Detected identities from order history</p>
            </div>
            <div className="flex flex-col">
              <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                  <div className="shadow overflow-hidden border-b border-gray-200 dark:border-gray-700 sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Identity Name
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Company
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Orders
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            GMV
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Address
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {(identities || []).map((identity: Identity, idx: number) => (
                          <tr key={idx} className={identity.selected ? "bg-green-50 dark:bg-green-900/10" : ""}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {identity.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {identity.company || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {identity.eligibleOrderCount !== undefined ? identity.eligibleOrderCount : identity.orderCount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(identity.gmv || 0)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" title={identity.address?.fullAddress || identity.fullAddress}>
                              {identity.address ? `${identity.address.street}, ${identity.address.postalCode} ${identity.address.city}, ${identity.address.country}` : identity.fullAddress || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {identity.selected ? (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                  Selected
                                </span>
                              ) : (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                  Excluded
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {(!identities || identities.length === 0) && (
                          <tr>
                            <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                              No identities found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </Tab.Panel>

          {/* DocuSign Panel */}
          <Tab.Panel className="rounded-xl bg-white dark:bg-gray-800 p-3 shadow ring-white dark:ring-gray-700 ring-opacity-60 ring-offset-2 ring-offset-blue-400 dark:ring-offset-gray-900 focus:outline-none">
            {isLoadingDocusign ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading DocuSign details...</div>
            ) : !docusignData ? (
               <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                 {user?.docusign?.envelopeId ? "Failed to load details from DocuSign." : "No DocuSign envelope associated with this user."}
               </div>
            ) : (
              <div className="space-y-6 px-4 py-5 sm:px-6">
                {/* Delivery Error Alert */}
                {hasDocusignError && (
                  <div className="rounded-md bg-orange-50 dark:bg-orange-900/20 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" aria-hidden="true" />
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-orange-800 dark:text-orange-200">
                          {user?.docusign?.deliveryStatus === 'authfailed' ? 'Identity Verification Failed' : 'DocuSign Delivery Issue'}
                        </h3>
                        <div className="mt-2 text-sm text-orange-700 dark:text-orange-300">
                          <p><strong>Status:</strong> {user?.docusign?.deliveryStatus}</p>
                          <p><strong>Error:</strong> {user?.docusign?.deliveryError || 'Unknown error'}</p>
                          {user?.docusign?.deliveryErrorDate && (
                            <p><strong>Date:</strong> {new Date(user.docusign.deliveryErrorDate).toLocaleString()}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Envelope Overview */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">Envelope Overview</h3>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => downloadContractMutation.mutate()}
                        disabled={downloadContractMutation.isPending}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                        {downloadContractMutation.isPending ? 'Downloading...' : 'Download Contract'}
                      </button>
                      <button
                        type="button"
                        onClick={() => identityEvidenceMutation.mutate()}
                        disabled={identityEvidenceMutation.isPending}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        <IdentificationIcon className="h-4 w-4 mr-2" />
                        {identityEvidenceMutation.isPending ? 'Loading...' : 'View ID'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsResendModalOpen(true)}
                        disabled={resendDocusignEmailMutation.isPending}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        <EnvelopeIcon className="h-4 w-4 mr-2" />
                        {resendDocusignEmailMutation.isPending ? 'Sending...' : 'Resend Signed Contract'}
                      </button>
                      {user?.progress?.completedAt?.contractStarted && (
                        <button
                          type="button"
                          onClick={() => {
                            setCorrectionData(getSelectedIdentityData());
                            setIsCorrectModalOpen(true);
                          }}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                        >
                          <PencilIcon className="h-4 w-4 mr-2" />
                          Correct & Recreate
                        </button>
                      )}
                    </div>
                  </div>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          docusignData.envelope?.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                          docusignData.envelope?.status === 'sent' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                          docusignData.envelope?.status === 'voided' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {docusignData.envelope?.status?.toUpperCase() || 'UNKNOWN'}
                        </span>
                      </dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Envelope ID</dt>
                      <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{docusignData.envelope?.envelopeId}</dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Subject</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">{docusignData.envelope?.emailSubject}</dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Sent Date</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                        {docusignData.envelope?.sentDateTime ? new Date(docusignData.envelope.sentDateTime).toLocaleString() : '-'}
                      </dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed Date</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                        {docusignData.envelope?.completedDateTime ? new Date(docusignData.envelope.completedDateTime).toLocaleString() : '-'}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Recipients */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">Recipients</h3>
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-6">Name & Email</th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Type</th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Status</th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Signed At</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                        {docusignData.recipients?.signers?.map((signer: { recipientId: string; name: string; email: string; status: string; signedDateTime?: string }) => (
                          <tr key={signer.recipientId}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                              <div className="font-medium text-gray-900 dark:text-white">{signer.name}</div>
                              <div className="text-gray-500 dark:text-gray-400">{signer.email}</div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">Signer</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                signer.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {signer.status}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                              {signer.signedDateTime ? new Date(signer.signedDateTime).toLocaleString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Custom Fields */}
                 {docusignData.customFields?.textCustomFields?.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">Custom Fields</h3>
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                      {docusignData.customFields.textCustomFields.map((field: { fieldId: string; name: string; value: string }) => (
                        <div key={field.fieldId} className="sm:col-span-1 border rounded p-3 dark:border-gray-700">
                           <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{field.name}</dt>
                           <dd className="mt-1 text-sm text-gray-900 dark:text-white">{field.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
                 
                 {docusignData.error && (
                    <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 mt-6">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error fetching some details</h3>
                          <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                            <p>{docusignData.error}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                 )}
              </div>
            )}
          </Tab.Panel>

          {/* History Panel */}
          <Tab.Panel className="rounded-xl bg-white dark:bg-gray-800 p-3 shadow ring-white dark:ring-gray-700 ring-opacity-60 ring-offset-2 ring-offset-blue-400 dark:ring-offset-gray-900 focus:outline-none">
            <div className="flow-root">
              <ul role="list" className="-mb-8">
                {historyData?.map((log: HistoryLog, logIdx: number) => (
                  <li key={log._id}>
                    <div className="relative pb-8">
                      {logIdx !== historyData.length - 1 ? (
                        <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
                      ) : null}
                      <div className="relative flex space-x-3">
                        <div>
                          <span className="h-8 w-8 rounded-full bg-gray-400 dark:bg-gray-600 flex items-center justify-center ring-8 ring-white dark:ring-gray-800">
                            <PencilIcon className="h-5 w-5 text-white" aria-hidden="true" />
                          </span>
                        </div>
                        <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Updated <span className="font-medium text-gray-900 dark:text-white">{log.field}</span> from "{formatHistoryValue(log.oldValue)}" to "{formatHistoryValue(log.newValue)}"
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Reason: {log.reason}</p>
                          </div>
                          <div className="whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                            <time dateTime={log.timestamp}>{new Date(log.timestamp).toLocaleString()}</time>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
                {(!historyData || historyData.length === 0) && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No history available.</div>
                )}
              </ul>
            </div>
          </Tab.Panel>

          {/* Communication Panel */}
          <Tab.Panel className="rounded-xl bg-white dark:bg-gray-800 p-3 shadow ring-white dark:ring-gray-700 ring-opacity-60 ring-offset-2 ring-offset-blue-400 dark:ring-offset-gray-900 focus:outline-none">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Send Email Form */}
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">Send Email</h3>
                <form onSubmit={handleSendEmail} className="space-y-4">
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Subject</label>
                    <input
                      type="text"
                      id="subject"
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Content (HTML)</label>
                    <textarea
                      id="content"
                      required
                      rows={6}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      value={emailContent}
                      onChange={(e) => setEmailContent(e.target.value)}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sendEmailMutation.isPending}
                    className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 dark:bg-indigo-500 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {sendEmailMutation.isPending ? "Sending..." : "Send Email"}
                  </button>
                </form>
              </div>

              {/* Communication Log */}
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">History</h3>
                <div className="flow-root">
                  <ul role="list" className="-mb-8">
                    {communicationData?.map((log: HistoryLog, logIdx: number) => (
                      <li key={log._id}>
                        <div className="relative pb-8">
                          {logIdx !== communicationData.length - 1 ? (
                            <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
                          ) : null}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className="h-8 w-8 rounded-full bg-blue-400 dark:bg-blue-600 flex items-center justify-center ring-8 ring-white dark:ring-gray-800">
                                <EnvelopeIcon className="h-5 w-5 text-white" aria-hidden="true" />
                              </span>
                            </div>
                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{log.subject}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-xs">{log.content?.replace(/<[^>]*>?/gm, '') || ''}</p>
                              </div>
                              <div className="whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                                <time dateTime={log.timestamp}>{new Date(log.timestamp).toLocaleString()}</time>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                    {(!communicationData || communicationData.length === 0) && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No communication history.</div>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      <EvidenceModal
        isOpen={isEvidenceModalOpen}
        onClose={() => setIsEvidenceModalOpen(false)}
        onConfirm={handleUpdateConfirm}
        title={`Update ${editingField}`}
      />

      {/* Dismiss Fraud Indicator Modal */}
      {dismissModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setDismissModal({ isOpen: false, mode: 'single' });
                setDismissReason("");
              }}
            />

            {/* Modal */}
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                    {dismissModal.mode === 'category'
                      ? `Dismiss All ${dismissModal.typeLabel}`
                      : 'Dismiss Fraud Indicator'}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {dismissModal.mode === 'category'
                        ? `You are about to dismiss ${dismissModal.count} indicator(s). This action will be logged.`
                        : 'You are about to dismiss this fraud indicator. This action will be logged.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="dismiss-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reason for dismissal <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="dismiss-reason"
                  rows={3}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="e.g., False positive - legitimate price variation due to sale event"
                  value={dismissReason}
                  onChange={(e) => setDismissReason(e.target.value)}
                />
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  disabled={!dismissReason.trim() || dismissFraudIndicatorMutation.isPending || dismissFraudIndicatorsByTypeMutation.isPending}
                  onClick={() => {
                    if (dismissModal.mode === 'category' && dismissModal.type) {
                      dismissFraudIndicatorsByTypeMutation.mutate({
                        type: dismissModal.type,
                        reason: dismissReason.trim()
                      });
                    } else if (dismissModal.mode === 'single' && dismissModal.index !== undefined) {
                      dismissFraudIndicatorMutation.mutate({
                        index: dismissModal.index,
                        reason: dismissReason.trim()
                      });
                    }
                  }}
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer sm:col-start-2"
                >
                  {dismissFraudIndicatorMutation.isPending || dismissFraudIndicatorsByTypeMutation.isPending
                    ? 'Dismissing...'
                    : dismissModal.mode === 'category'
                    ? `Dismiss ${dismissModal.count} Indicator(s)`
                    : 'Dismiss Indicator'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDismissModal({ isOpen: false, mode: 'single' });
                    setDismissReason("");
                  }}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:col-start-1 sm:mt-0 cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Identity Evidence Modal */}
      {isIdentityEvidenceModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsIdentityEvidenceModalOpen(false)}
            />

            {/* Modal */}
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsIdentityEvidenceModalOpen(false)}
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                  <IdentificationIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                    Identity Verification Evidence
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      ID documents submitted during DocuSign identity verification.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                {identityEvidenceImages.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {identityEvidenceImages.map((img, idx) => (
                      <div key={idx} className="border rounded-lg p-2 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 capitalize">{img.type}</p>
                        <img
                          src={img.data}
                          alt={`ID Document ${idx + 1}`}
                          className="w-full rounded"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No identity evidence images available.
                  </p>
                )}
              </div>

              <div className="mt-5 sm:mt-6">
                <button
                  type="button"
                  onClick={() => setIsIdentityEvidenceModalOpen(false)}
                  className="inline-flex w-full justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resend Signed Contract Confirmation Modal */}
      {isResendModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsResendModalOpen(false)}
            />

            {/* Modal */}
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
              <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30">
                  <EnvelopeIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                    Resend Signed Contract
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      This will send a copy of the signed contract to the claimant's email address.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  disabled={resendDocusignEmailMutation.isPending}
                  onClick={() => {
                    resendDocusignEmailMutation.mutate();
                    setIsResendModalOpen(false);
                  }}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer sm:col-start-2"
                >
                  {resendDocusignEmailMutation.isPending ? 'Sending...' : 'Send'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsResendModalOpen(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:col-start-1 sm:mt-0 cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Correct DocuSign Details Modal */}
      {isCorrectModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setIsCorrectModalOpen(false);
                setCorrectionData({ name: "", street: "", city: "", postalCode: "", country: "DE" });
              }}
            />

            {/* Modal */}
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30">
                  <PencilIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                    Correct Details & Recreate Envelope
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Update the details below. This will void the current envelope and create a new one with the corrected document.
                    </p>
                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-2">
                      Note: This will incur a new envelope and IDV charge.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-4">
                <div>
                  <label htmlFor="correct-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="correct-name"
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    value={correctionData.name}
                    onChange={(e) => setCorrectionData({ ...correctionData, name: e.target.value })}
                  />
                </div>

                <div>
                  <label htmlFor="correct-street" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Street
                  </label>
                  <input
                    type="text"
                    id="correct-street"
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    value={correctionData.street}
                    onChange={(e) => setCorrectionData({ ...correctionData, street: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="correct-postalcode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      id="correct-postalcode"
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      value={correctionData.postalCode}
                      onChange={(e) => setCorrectionData({ ...correctionData, postalCode: e.target.value })}
                    />
                  </div>

                  <div>
                    <label htmlFor="correct-city" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      id="correct-city"
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      value={correctionData.city}
                      onChange={(e) => setCorrectionData({ ...correctionData, city: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="correct-country" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    id="correct-country"
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    value={correctionData.country}
                    onChange={(e) => setCorrectionData({ ...correctionData, country: e.target.value })}
                  />
                </div>
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  disabled={!correctionData.name.trim() || correctDocusignMutation.isPending}
                  onClick={() => {
                    if (window.confirm("Are you sure you want to void the current envelope and create a new one? This will incur additional charges.")) {
                      correctDocusignMutation.mutate(correctionData);
                    }
                  }}
                  className="inline-flex w-full justify-center rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer sm:col-start-2"
                >
                  {correctDocusignMutation.isPending ? 'Recreating...' : 'Void & Recreate'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsCorrectModalOpen(false);
                    setCorrectionData({ name: "", street: "", city: "", postalCode: "", country: "DE" });
                  }}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:col-start-1 sm:mt-0 cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
